/**
 * Broker First Sold Opportunity CSV Export
 *
 * For each broker with at least one won opportunity, finds their first win
 * and exports a flat CSV with broker context, opportunity details, and
 * derived metrics (days to first win, total won opps/groups).
 */

import { dataverseClient, activeOnly } from '@shared/api';
import { contactApi } from '@sales/entities/contact';

// =============================================================================
// Types
// =============================================================================

interface BrokerEntity {
  contactid: string;
  fullname: string;
  _parentcustomerid_value?: string;
  _ownerid_value?: string;
  address1_city?: string;
  address1_stateorprovince?: string;
  customertypecode?: number;
  createdon?: string;
  // OData formatted value annotations
  [key: string]: unknown;
}

interface OppEntity {
  opportunityid: string;
  name: string;
  _parentcontactid_value?: string;
  _parentaccountid_value?: string;
  _ownerid_value?: string;
  _dts_group_value?: string;
  statecode: number;
  actualclosedate?: string;
  dts_effectivedate?: string;
  createdon?: string;
  dts_numberofemployees?: number;
  dts_renewal?: number | null;
  // Product toggles
  dts_tpaadmin?: boolean;
  dts_veracityrx?: boolean;
  dts_veracityonsite?: boolean;
  dts_veracityrisk?: boolean;
  dts_providersofexcellence?: boolean;
  dts_minimumessentialcoveragemec?: boolean;
  dts_maplebridge?: boolean;
  // OData formatted value annotations
  [key: string]: unknown;
}

// =============================================================================
// Helpers
// =============================================================================

function escapeCsvCell(value: string): string {
  // Prevent CSV injection: prefix formula-triggering characters (OWASP CSV Injection)
  if (/^[=+\-@\t\r]/.test(value)) {
    value = `'${value}`;
  }
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDate(d?: string): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString();
}

function daysBetween(d1: string, d2: string): number {
  return Math.floor(Math.abs(new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

function getProducts(opp: OppEntity): string {
  const products: string[] = [];
  if (opp.dts_tpaadmin) products.push('TPA Admin');
  if (opp.dts_veracityrx) products.push('Veracity Rx');
  if (opp.dts_veracityonsite) products.push('Veracity OnSite');
  if (opp.dts_veracityrisk) products.push('Veracity Risk');
  if (opp.dts_providersofexcellence) products.push('Providers of Excellence');
  if (opp.dts_minimumessentialcoveragemec) products.push('MEC');
  if (opp.dts_maplebridge) products.push('Maplebridge');
  return products.join('; ');
}

// =============================================================================
// Export
// =============================================================================

const HEADERS = [
  'Broker Name',
  'Broker Company',
  'Broker Owner',
  'Broker City',
  'Broker State',
  'Broker Added Date',
  'First Won Opp Name',
  'Account / Group',
  'Opp Owner',
  'Effective Date',
  'Close Date',
  'Employees',
  'Products',
  'Is Renewal',
  'Days from Broker Added to First Win',
  'Total Won Opps',
  'Total Won Groups',
];

export async function exportBrokerFirstWon(): Promise<string> {
  const brokerTypeCode = await contactApi.ensureBrokerTypeCode();
  const brokers = await dataverseClient.listAll<BrokerEntity>('contacts', {
    select: [
      'contactid', 'fullname',
      '_parentcustomerid_value', '_ownerid_value',
      'emailaddress1', 'telephone1',
      'address1_city', 'address1_stateorprovince',
      'customertypecode', 'createdon',
    ],
    filter: `${activeOnly()} and customertypecode eq ${brokerTypeCode}`,
    orderBy: 'fullname asc',
  });

  // Fetch all won opportunities
  const wonOpps = await dataverseClient.listAll<OppEntity>('opportunities', {
    select: [
      'opportunityid', 'name',
      '_parentcontactid_value', '_parentaccountid_value', '_ownerid_value',
      '_dts_group_value',
      'statecode', 'actualclosedate', 'dts_effectivedate', 'createdon',
      'dts_numberofemployees', 'dts_renewal',
      'dts_tpaadmin', 'dts_veracityrx', 'dts_veracityonsite', 'dts_veracityrisk',
      'dts_providersofexcellence', 'dts_minimumessentialcoveragemec', 'dts_maplebridge',
    ],
    filter: 'statecode eq 1',
    orderBy: 'actualclosedate asc',
  });

  // Group won opps by broker
  const oppsByBroker = new Map<string, OppEntity[]>();
  for (const opp of wonOpps) {
    if (!opp._parentcontactid_value) continue;
    const list = oppsByBroker.get(opp._parentcontactid_value) ?? [];
    list.push(opp);
    oppsByBroker.set(opp._parentcontactid_value, list);
  }

  const rows: string[][] = [HEADERS];

  for (const broker of brokers) {
    const opps = oppsByBroker.get(broker.contactid);
    if (!opps || opps.length === 0) continue;

    // First won = earliest actualclosedate
    const sorted = opps.slice().sort((a, b) =>
      (a.actualclosedate ?? '').localeCompare(b.actualclosedate ?? ''),
    );
    const first = sorted[0]!;

    // Count unique won groups
    const wonGroupIds = new Set(
      opps.map((o) => o._dts_group_value ?? o._parentaccountid_value).filter(Boolean),
    );

    const daysToFirstWin = broker.createdon && first.actualclosedate
      ? String(daysBetween(broker.createdon, first.actualclosedate))
      : '';

    const fv = (entity: Record<string, unknown>, field: string) =>
      (entity[`${field}@OData.Community.Display.V1.FormattedValue`] as string) ?? '';

    const accountOrGroup = fv(first, '_dts_group_value') || fv(first, '_parentaccountid_value');

    rows.push([
      broker.fullname ?? '',
      fv(broker, '_parentcustomerid_value'),
      fv(broker, '_ownerid_value'),
      broker.address1_city ?? '',
      broker.address1_stateorprovince ?? '',
      formatDate(broker.createdon),
      first.name ?? '',
      accountOrGroup,
      fv(first, '_ownerid_value'),
      formatDate(first.dts_effectivedate),
      formatDate(first.actualclosedate),
      first.dts_numberofemployees != null ? String(first.dts_numberofemployees) : '',
      getProducts(first),
      first.dts_renewal === 1 ? 'Yes' : first.dts_renewal === 0 ? 'No' : '',
      daysToFirstWin,
      String(opps.length),
      String(wonGroupIds.size),
    ]);
  }

  return rows.map((row) => row.map((cell) => escapeCsvCell(cell)).join(',')).join('\n');
}

export function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
